import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { slugify } from '@/lib/legal-utils';
import { cn } from '@/lib/utils';
import type { Components } from 'react-markdown';

interface LegalDocumentRendererProps {
  content: string;
  className?: string;
}

export function LegalDocumentRenderer({ content, className }: LegalDocumentRendererProps) {
  const components: Components = {
    h1: ({ children }) => {
      const text = extractText(children);
      return (
        <h1 
          id={slugify(text)} 
          className="scroll-mt-24 text-3xl font-bold text-foreground mt-8 mb-4"
        >
          {children}
        </h1>
      );
    },
    h2: ({ children }) => {
      const text = extractText(children);
      return (
        <h2 
          id={slugify(text)} 
          className="scroll-mt-24 text-2xl font-bold text-foreground mt-8 mb-4"
        >
          {children}
        </h2>
      );
    },
    h3: ({ children }) => {
      const text = extractText(children);
      return (
        <h3 
          id={slugify(text)} 
          className="scroll-mt-24 text-xl font-semibold text-foreground mt-6 mb-3"
        >
          {children}
        </h3>
      );
    },
    h4: ({ children }) => {
      const text = extractText(children);
      return (
        <h4 
          id={slugify(text)} 
          className="scroll-mt-24 text-lg font-semibold text-foreground mt-4 mb-2"
        >
          {children}
        </h4>
      );
    },
    p: ({ children }) => (
      <p className="mb-4 leading-relaxed text-muted-foreground">
        {children}
      </p>
    ),
    ul: ({ children }) => (
      <ul className="my-4 ml-6 list-disc space-y-1.5">
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className="my-4 ml-6 list-decimal space-y-1.5">
        {children}
      </ol>
    ),
    li: ({ children }) => (
      <li className="text-muted-foreground leading-relaxed">
        {children}
      </li>
    ),
    strong: ({ children }) => (
      <strong className="font-semibold text-foreground">
        {children}
      </strong>
    ),
    a: ({ href, children }) => (
      <a 
        href={href} 
        className="text-primary hover:underline"
        target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      >
        {children}
      </a>
    ),
    table: ({ children }) => (
      <div className="my-6 w-full overflow-x-auto">
        <table className="w-full border-collapse">
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => (
      <thead className="bg-muted/50">
        {children}
      </thead>
    ),
    tbody: ({ children }) => (
      <tbody>
        {children}
      </tbody>
    ),
    tr: ({ children }) => (
      <tr className="border-b border-border">
        {children}
      </tr>
    ),
    th: ({ children }) => (
      <th className="border border-border px-4 py-2 text-left font-semibold text-foreground">
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className="border border-border px-4 py-2 text-muted-foreground">
        {children}
      </td>
    ),
    blockquote: ({ children }) => (
      <blockquote className="my-4 border-l-4 border-primary/30 pl-4 italic text-muted-foreground">
        {children}
      </blockquote>
    ),
    hr: () => (
      <hr className="my-8 border-border" />
    ),
    code: ({ children }) => (
      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm">
        {children}
      </code>
    ),
  };

  return (
    <div className={cn("max-w-none", className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Extract plain text from React children for slugification
 */
function extractText(children: React.ReactNode): string {
  if (typeof children === 'string') {
    return children;
  }
  if (Array.isArray(children)) {
    return children.map(extractText).join('');
  }
  if (children && typeof children === 'object' && 'props' in children) {
    return extractText((children as React.ReactElement).props.children);
  }
  return '';
}
